import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import Cookies from 'js-cookie';
import { brandDomain, getSiteSettings } from '@/brands';

// Requests (dev and production) go to VITE_API_BASE_URL (the deployed backend host, which
// serves at root with no `/api` prefix). If it's empty, falls back to same-origin.
const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL || '',
  prepareHeaders: (headers) => {
    const token = Cookies.get('access-token');

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    // White-label: tell the backend which brand domain this app is served on.
    if (typeof window !== 'undefined') {
      headers.set('X-Brand-Domain', brandDomain());
    }
    return headers;
  },
});

// One bounce per document. `window.location.href` below reloads the page, so
// this resets naturally — it only stops several 401s in the same tick from
// stacking navigations on top of each other.
let bouncing = false;

const baseQueryWithAuth = async (args: any, api: any, extraOptions: any) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result?.error?.status === 401) {
    const hadToken = !!Cookies.get('access-token');
    Cookies.remove('access-token');

    // Only a 401 we were carrying a token for is an expired session worth
    // bouncing on. A 401 with no token means the query should never have run
    // while logged out, and redirecting on it loops forever: /login does not
    // render a page, it opens the sheet and replaces the URL with /home (see
    // LoginPage), so the `pathname !== target` guard below can never hold and
    // the reloaded /home fires the very same request again. Let it fail.
    if (hadToken && !bouncing) {
      // 🔁 redirect to login — unless the brand has the login page switched off,
      // in which case /login is not mounted and this would land on the 404 page.
      const target = getSiteSettings().showLogin ? '/login' : '/home';
      if (window.location.pathname !== target) {
        bouncing = true;
        window.location.href = target;
      }
    }
  }

  return result;
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['User', 'Auth', 'FeatureFlags', 'Sports', 'Bets', 'Account', 'Content', 'FD', 'Affiliate'],
  endpoints: () => ({}),
});

// Export hooks for usage in functional components
export const {
  util: { getRunningQueriesThunk },
} = apiSlice;
// Export the api slice for use in store
export default apiSlice;
